import { AxiosError } from 'axios';
import { VoteCandidate } from '../types';
import apiClient from './api';

interface VotePairResponse {
  pair_id: string;
  left: VoteCandidate;
  right: VoteCandidate;
}

interface SubmitVotePayload {
  pair_id: string;
  winner_id: number;
  loser_id: number;
  voter_id: number;
}

interface SubmitVoteResponse {
  new_elo: {
    winner: number;
    loser: number;
  };
}

interface VoteStatsResponse {
  total: number;
  this_week: number;
  today: number;
}

/**
 * Retrieves a pair of students for voting comparison.
 * @param studentClass - Optional class year (e.g., 2026).
 * @param voterId - The ID of the voter making the request.
 * @param position - Optional position filter (e.g., 'engineer').
 * @param voterId - The ID of the voter making the request.
 * @returns A promise resolving to the vote pair.
 */
export const getVotePair = async (
  studentClass: number | undefined,
  voterId: number,
  position?: string
): Promise<VotePairResponse> => {
  console.log(`[vote.ts] getVotePair called with class: ${studentClass}, voterId: ${voterId}, position: ${position}`);
  try {
    const params: { class?: number; voter_id: number; position?: string; } = {
      voter_id: voterId
    };
    if (studentClass !== undefined) {
      params.class = studentClass;
    }
    if (position) {
      params.position = position;
    }
    const response = await apiClient.get<VotePairResponse>('/api/vote-pair', { params });
    console.log('[vote.ts] getVotePair successful:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('[vote.ts] getVotePair failed:', error);
    if (error instanceof AxiosError) {
      if (error.response) {
        console.error('Error fetching vote pair:', error.response.data);
        const errorData = error.response.data;
        return Promise.reject(new Error(errorData.message || 'Failed to load pair'));
      } else {
        console.error('Axios error (no response):', error.message);
        return Promise.reject(new Error('Network error, please try again'));
      }
    } else if (error instanceof Error) {
      console.error('An unexpected error occurred:', error.message);
      return Promise.reject(error);
    } else {
      console.error('An unexpected non-error was thrown:', error);
      return Promise.reject(new Error('An unexpected error occurred'));
    }
  }
};

/**
 * Submits a vote between a pair of students.
 * @param payload - The vote submission details.
 * @returns A promise resolving to the new Elo ratings and vote count.
 */
export const submitVote = async (
  payload: SubmitVotePayload
): Promise<SubmitVoteResponse> => {
  console.log('[vote.ts] submitVote called with payload:', JSON.stringify(payload, null, 2));
  try {
    const response = await apiClient.post<SubmitVoteResponse>('/api/votes', payload);
    console.log('[vote.ts] submitVote successful:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('[vote.ts] submitVote failed:', error);
    if (error instanceof AxiosError) {
      if (error.response) {
        console.error('Error submitting vote:', error.response.data);
        if (error.response.status === 409) {
          return error.response.data;
        }
        throw error.response.data;
      } else {
        console.error('Axios error (no response):', error.message);
        throw new Error(`Network error submitting vote: ${error.message}`);
      }
    } else if (error instanceof Error) {
      console.error('An unexpected error occurred:', error.message);
      throw new Error(`An unexpected error occurred while submitting vote: ${error.message}`);
    } else {
      console.error('An unexpected non-error was thrown:', error);
      throw new Error('An unexpected non-error occurred while submitting vote.');
    }
  }
};

/**
 * Gets a summary of the user's voting history.
 * @returns A promise resolving to the vote statistics.
 */
export const getVoteStats = async (): Promise<VoteStatsResponse> => {
  console.log('[vote.ts] getVoteStats called');
  try {
    const response = await apiClient.get<VoteStatsResponse>('/api/votes/stats');
    console.log('[vote.ts] getVoteStats successful:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('[vote.ts] getVoteStats failed:', error);
    if (error instanceof AxiosError) {
      if (error.response) {
        console.error('Error fetching vote stats:', error.response.data);
        throw error.response.data;
      } else {
        console.error('Axios error (no response):', error.message);
        throw new Error(`Network error fetching vote stats: ${error.message}`);
      }
    } else if (error instanceof Error) {
      console.error('An unexpected error occurred:', error.message);
      throw new Error(`An unexpected error occurred while fetching vote stats: ${error.message}`);
    } else {
      console.error('An unexpected non-error was thrown:', error);
      throw new Error('An unexpected non-error occurred while fetching vote stats.');
    }
  }
};